function escapeHtmlForPrint(s) {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Resolves a stored select value (top-level answer id or opt-group child id) to a display label.
 * Matches FormResponseView select handling and QuestionRenderers select OptGroup child ids.
 * @param {object} question - Form schema question
 * @param {string} raw - Stored value
 */
export function resolveSelectStoredValue(question, raw) {
  if (raw == null || raw === '') return null;
  const answers = question?.questionOptions?.answers;
  if (!Array.isArray(answers)) return null;

  for (let ai = 0; ai < answers.length; ai++) {
    const answer = answers[ai];
    const answerId = answer.concept || answer.conceptCode || `answer_${ai}`;
    if (answerId === raw) {
      const term = answer.terminology;
      return {
        answerLabel: answer.label,
        terminology: term ? { code: term.code, display: term.display } : null,
      };
    }
    if (!answer.children?.length) continue;
    for (let ci = 0; ci < answer.children.length; ci++) {
      const child = answer.children[ci];
      // Match FormResponseView / QuestionRenderers: stored value is usually the synthetic
      // `${answerId}_child_${ci}` when concepts are empty, not the optional long `child.id`.
      const syntheticChildId = `${answerId}_child_${ci}`;
      const explicitChildId = child.concept || child.conceptCode;
      const matches =
        raw === explicitChildId ||
        raw === syntheticChildId ||
        (child.id != null && child.id !== '' && raw === child.id);
      if (matches) {
        const combined = `${answer.label || ''} ${child.label || ''}`.trim();
        const term = child.terminology || answer.terminology;
        return {
          answerLabel: combined,
          terminology: term ? { code: term.code, display: term.display } : null,
        };
      }
    }
  }
  return null;
}

/** FormField-style options on a schema child (value may be label, concept, or generated option id). */
export function resolveChildOptionsStoredValue(child, raw) {
  if (raw == null || raw === '' || typeof raw !== 'string') return null;
  if (!child.options?.length) return null;
  for (let oi = 0; oi < child.options.length; oi++) {
    const opt = child.options[oi];
    const optValue = opt.concept || opt.conceptCode || opt.label || `option_${oi}`;
    if (optValue === raw || opt.label === raw) {
      return opt.label != null ? String(opt.label) : raw;
    }
  }
  return null;
}

/**
 * Renders collection `items` (shape from transformFormSubmissionForPrinter) as HTML for configs
 * that use a bloc with {{{htmlContent}}} (e.g. org-specific form_submission.json).
 */
export function serializePrintSectionItemsToHtml(items) {
  if (!Array.isArray(items) || items.length === 0) {
    return '<p style="color:#999;font-style:italic;">No form sections to display.</p>';
  }
  const parts = [];
  items.forEach((item) => {
    const { sectionLabel, questions = [], pageLabel, showPageHeading } = item?.content || {};
    let block = '';
    if (showPageHeading && pageLabel) {
      block += `<h2 style="margin:0.65em 0 0.35em 0;font-size:1rem;font-weight:700;color:#003087;border-bottom:1px solid #d8dde0;padding-bottom:0.25rem;">${escapeHtmlForPrint(pageLabel)}</h2>`;
    }
    block += `<div class="print-section" style="margin-bottom:1.5em;"><h3 style="margin:0 0 0.5em 0;">${escapeHtmlForPrint(sectionLabel || 'Section')}</h3>`;
    questions.forEach((q) => {
      const qLabel = escapeHtmlForPrint(q.questionLabel || 'Question');
      let inner = '';
      if (q.answers?.length) {
        inner = q.answers
          .map((a) => {
            const label = escapeHtmlForPrint(a.answerLabel || '');
            const nest = a.nestedValue != null && a.nestedValue !== '' ? `: ${escapeHtmlForPrint(a.nestedValue)}` : '';
            return `<div style="margin:0.25em 0;">✓ <span>${label}</span><span>${nest}</span></div>`;
          })
          .join('');
      } else if (q.value !== undefined && q.value !== null && q.value !== '') {
        const v =
          typeof q.value === 'object' ? escapeHtmlForPrint(JSON.stringify(q.value)) : escapeHtmlForPrint(q.value);
        inner = `<div>${v}</div>`;
      } else {
        inner = '<div style="color:#999;font-style:italic;">No answer provided</div>';
      }
      block += `<div style="margin-bottom:0.75em;"><strong>${qLabel}</strong>${inner}</div>`;
    });
    block += '</div>';
    parts.push(block);
  });
  return parts.join('');
}

/**
 * Transform form submission data for printer
 */
export const transformFormSubmissionForPrinter = (formSchema, formValues) => {
  const sections = [];

  if (!formSchema?.pages) return { sections: { type: 'collection', content: { items: sections } } };

  const processNestedChildren = (children, storeKey, keyPrefix, owningQuestion) => {
    if (!children?.length) return [];

    const nestedAnswers = [];
    children.forEach((child, ci) => {
      // Matches QuestionRenderers createChildrenRenderer: childKeyPrefix + index fallback
      const childId =
        child.id ||
        child.concept ||
        child.conceptCode ||
        (keyPrefix != null && keyPrefix !== ''
          ? `${keyPrefix}_child_${ci}`
          : `${storeKey}_child_${ci}`);

      if (child.componentType) {
        const fieldId = `${storeKey}_${childId}`;
        const childValue = formValues[fieldId] || formValues[storeKey]?.[childId];

        if (!childValue) return;

        let displayValue = childValue;
        if (typeof childValue === 'object' && childValue !== null) {
          if (childValue.value !== undefined) {
            displayValue = childValue.value;
            if (childValue.otherValue) {
              displayValue = `${displayValue}: ${childValue.otherValue}`;
            }
          } else if (childValue.otherValue !== undefined) {
            displayValue = childValue.otherValue;
          } else if (Object.keys(childValue).length > 0) {
            const meaningfulKeys = ['label', 'text', 'name', 'display', 'value'];
            const foundKey = meaningfulKeys.find((key) => childValue[key]);
            displayValue = foundKey ? childValue[foundKey] : JSON.stringify(childValue);
          }
        }

        if (typeof displayValue === 'string' && owningQuestion) {
          const fromOptions = resolveChildOptionsStoredValue(child, displayValue);
          if (fromOptions != null) {
            displayValue = fromOptions;
          } else {
            const fromQuestionSelect = resolveSelectStoredValue(owningQuestion, displayValue);
            if (fromQuestionSelect) {
              displayValue = fromQuestionSelect.answerLabel;
            }
          }
        }

        const childAnswer = {
          selected: true,
          answerLabel: child.label || child.componentType,
          terminology: child.terminology
            ? {
                code: child.terminology.code,
                display: child.terminology.display,
              }
            : null,
          nestedValue: displayValue,
          children: [],
        };

        if (child.children?.length > 0) {
          childAnswer.children = processNestedChildren(
            child.children,
            fieldId,
            childId,
            owningQuestion
          );
        }

        nestedAnswers.push(childAnswer);
      } else {
        const childValue = formValues[storeKey]?.[childId];

        if (
          childValue === true ||
          (typeof childValue === 'object' && childValue !== null && childValue.checked !== false)
        ) {
          const childAnswer = {
            selected: true,
            answerLabel: child.label,
            terminology: child.terminology
              ? {
                  code: child.terminology.code,
                  display: child.terminology.display,
                }
              : null,
            children: [],
          };

          if (child.children?.length > 0) {
            childAnswer.children = processNestedChildren(
              child.children,
              storeKey,
              childId,
              owningQuestion
            );
          }

          nestedAnswers.push(childAnswer);
        }
      }
    });

    return nestedAnswers;
  };

  formSchema.pages.forEach(page => {
    if (!page.sections) return;

    const pageLabel = page.label || '';
    page.sections.forEach((section, sectionIndex) => {
      const sectionData = {
        sectionLabel: section.label || 'Untitled Section',
        pageLabel,
        showPageHeading: Boolean(pageLabel) && sectionIndex === 0,
        questions: []
      };

      if (section.questions) {
        section.questions.forEach(question => {
          const questionValue = formValues?.[question.id];
          const hasValue = (() => {
            if (questionValue === undefined || questionValue === null) return false;
            if (typeof questionValue === 'string' && questionValue.trim() === '') return false;
            if (typeof questionValue === 'number' || typeof questionValue === 'boolean') return true;
            if (typeof questionValue === 'object' && !Array.isArray(questionValue)) {
              return Object.keys(questionValue).length > 0;
            }
            return true;
          })();

          const layout = question.questionOptions?.layout || 'vertical';
          const questionData = {
            questionLabel: question.label || 'Untitled Question',
            answers: [],
            value: null,
            layout: layout,
            isHorizontal: layout === 'horizontal'
          };

          const rendering = question.questionOptions?.rendering;

          // Only process answers if there's a value
          if (!hasValue) {
            // Leave questionData.answers empty and questionData.value null
            // Template will show "No answer provided"
          } else {
            switch (rendering) {
            case 'checkbox':
              question.questionOptions?.answers?.forEach((answer, idx) => {
                const answerId = answer.concept || answer.conceptCode || `answer_${idx}`;
                if (questionValue[answerId]) {
                  const answerData = {
                    selected: true,
                    answerLabel: answer.label,
                    terminology: answer.terminology ? {
                      code: answer.terminology.code,
                      display: answer.terminology.display
                    } : null,
                    children: []
                  };
                  
                  if (answer.children?.length > 0) {
                    answerData.children = processNestedChildren(
                      answer.children,
                      question.id,
                      answerId,
                      question
                    );
                  }
                  
                  questionData.answers.push(answerData);
                }
              });
              
              const otherValue = questionValue['__other__'];
              if (otherValue?.checked) {
                questionData.answers.push({
                  selected: true,
                  answerLabel: question.questionOptions?.otherLabel || 'Other',
                  nestedValue: otherValue.otherValue || '(not specified)'
                });
              }
              break;

            case 'radio':
              const radioValue = questionValue;
              const isOtherSelected = typeof radioValue === 'object' && radioValue?.value === '__other__';
              
              if (isOtherSelected) {
                questionData.answers.push({
                  selected: true,
                  answerLabel: question.questionOptions?.otherLabel || 'Other',
                  nestedValue: radioValue?.otherValue || '(not specified)'
                });
              } else {
                let selectedAnswerId = null;
                const selectedAnswer = question.questionOptions?.answers?.find((answer, idx) => {
                  const answerId = answer.concept || answer.conceptCode || `answer_${idx}`;
                  if (answerId === radioValue) {
                    selectedAnswerId = answerId;
                    return true;
                  }
                  return false;
                });

                if (selectedAnswer) {
                  const answerData = {
                    selected: true,
                    answerLabel: selectedAnswer.label,
                    terminology: selectedAnswer.terminology ? {
                      code: selectedAnswer.terminology.code,
                      display: selectedAnswer.terminology.display
                    } : null,
                    children: []
                  };
                  
                  if (selectedAnswer.children?.length > 0) {
                    const childrenStoreKey = `${question.id}_children`;
                    answerData.children = processNestedChildren(
                      selectedAnswer.children,
                      childrenStoreKey,
                      selectedAnswerId,
                      question
                    );
                  }
                  
                  questionData.answers.push(answerData);
                }
              }
              break;

            case 'select':
              const selectValue = questionValue;
              const isSelectOther = typeof selectValue === 'object' && selectValue?.value === '__other__';
              const displayValue = typeof selectValue === 'object' ? selectValue?.value : selectValue;
              
              if (isSelectOther) {
                questionData.answers.push({
                  selected: true,
                  answerLabel: question.questionOptions?.otherLabel || 'Other',
                  nestedValue: selectValue?.otherValue || '(not specified)'
                });
              } else {
                const selectedAnswer = question.questionOptions?.answers?.find((answer, idx) => {
                  const answerId = answer.concept || answer.conceptCode || `answer_${idx}`;
                  return answerId === displayValue;
                });

                if (selectedAnswer) {
                  questionData.answers.push({
                    selected: true,
                    answerLabel: selectedAnswer.label,
                    terminology: selectedAnswer.terminology ? {
                      code: selectedAnswer.terminology.code,
                      display: selectedAnswer.terminology.display
                    } : null
                  });
                } else {
                  const resolved = resolveSelectStoredValue(question, displayValue);
                  if (resolved) {
                    questionData.answers.push({
                      selected: true,
                      answerLabel: resolved.answerLabel,
                      terminology: resolved.terminology,
                    });
                  } else {
                    questionData.value = displayValue;
                  }
                }
              }
              break;

            case 'checkbox-with-select':
              question.questionOptions?.answers?.forEach((answer, idx) => {
                const answerId = answer.checkboxConceptCode || answer.checkboxTerminology?.id || `checkbox-select_${idx}`;
                const checkboxValue = questionValue[answerId];
                
                if (checkboxValue?.checked) {
                  const selectValue = checkboxValue?.selectValue;
                  const isOther = typeof selectValue === 'object' && selectValue?.value === '__other__';
                  
                  let nestedValue = null;
                  if (selectValue) {
                    if (isOther) {
                      nestedValue = `${question.questionOptions?.otherLabel || 'Other'}: ${selectValue?.otherValue || '(not specified)'}`;
                    } else {
                      const selectedOption = answer.selectOptions?.find(opt => 
                        (opt.label || `option_${idx}`) === selectValue
                      );
                      nestedValue = selectedOption?.label || selectValue;
                    }
                  }
                  
                  questionData.answers.push({
                    selected: true,
                    answerLabel: answer.checkboxLabel || answer.checkboxTerminology?.display || 'Checkbox Label',
                    terminology: answer.checkboxTerminology ? {
                      code: answer.checkboxTerminology.code,
                      display: answer.checkboxTerminology.display
                    } : null,
                    nestedValue
                  });
                }
              });
              break;

            default:
              questionData.value = questionValue;
              break;
            }
          }

          // Always add the question, even if no answer (will show "No answer provided")
          sectionData.questions.push(questionData);
        });
      }

      // Always add sections, even if no questions (will show "No questions in this section")
      // Wrap in { content: ... } as expected by printer's collection processor
      sections.push({ content: sectionData });
    });
  });

  return {
    sections: {
      type: 'collection',
      content: {
        items: sections
      }
    }
  };
};

